import { FISHING_CONTEST_SELECTION_CHANGE_EVENT, FISHING_CONTEST_VOTING_CLOSE_EVENT, fishingContestSelectionForQuery } from "../fishing-contest/contract.js";
import { contestQueryLabelMarkup } from "../fishing-contest/query-presentation.js";
import { mountFishQueryPresentation, fishQueryRecordMarkup, fishQueryFilterMarkup } from "./query-presentation.js";
import { queryInputMarkup, querySuggestionRowsMarkup } from "../query/presentation.js";
import { readBoundedAccountJson } from "../account/account-state-session-repository.js";
import { fishQueryCompletion, applicableFishQuery, fishQueryIndex, compileFishQuery, matchingQueryRecords, fishQueryNameGroup, QUERY_STATUSES, QUERY_GRADES, QUERY_METHODS } from "./query.js";

function decodeZones(value, revision, locale) {
  if (value?.schema !== "fishystuff.fishydex-query-zones.v1" || Object.keys(value).sort().join(",") !== "locale,revision,schema,zones" || value.revision !== revision.replace(/^dolt:/, "") || value.locale !== locale
    || !Array.isArray(value.zones) || value.zones.length > 16384) throw new TypeError("Invalid Fishydex zone index revision or schema");
  for (const zone of value.zones) {
    if (Object.keys(zone).sort().join(",") !== "known,name,potential,rgb" || typeof zone.rgb !== "string" || !/^\d{1,3},\d{1,3},\d{1,3}$/.test(zone.rgb) || zone.rgb.split(",").some(channel => Number(channel) > 255) || (zone.name !== null && (typeof zone.name !== "string" || zone.name.length > 1024))
      || ![zone.known, zone.potential].every(ids => Array.isArray(ids) && ids.length <= 4096 && ids.every(id => Number.isSafeInteger(id) && id > 0))) {
      throw new TypeError("Invalid Fishydex zone membership");
    }
  }
  return value.zones;
}
export function mountFishydexQuery({ root, changed, t }) {
  const host = root.querySelector('[data-fishydex-query-input]');
  host.innerHTML = queryInputMarkup({ prefix: "fishydex", inputClass: "fishydex-search", placeholderKey: "fishydex.query.placeholder", labelKey: "fishydex.query.label", label: t("fishydex.query.label"), statusId: "fishydex-query-status-detail", maxLength: 160 });
  const input = host.querySelector('.fishydex-search');
  const panel = host.querySelector('#fishydex-query-suggestions-shell');
  const clear = host.querySelector('#fishydex-query-clear');
  clear.setAttribute('data-fishydex-clear-filters', '');
  clear.setAttribute('aria-label', t('map.query.clear'));
  clear.setAttribute('data-fishy-tooltip', t('map.query.clear'));
  input.placeholder = t('fishydex.query.placeholder');
  const status = root.querySelector('[data-query-status]');
  const options = root.querySelector('#fishydex-query-suggestions');
  let fishSource, titleSource, index, parsedIndex, source, parsed;
  const presentation = mountFishQueryPresentation({ host, status, input, t });
  let titles = [], titleKind = "loading", zones = [], zoneKey = "", zoneKind = "empty", request;
  let disposed = false, votingFromQuery = false;
  const contest = root.querySelector("fishy-fishing-contest");
  const lifecycle = new AbortController();
  let candidates = [], highlighted = -1, editing = false;
  const listen = (node, event, callback) => node.addEventListener(event, callback, { signal: lifecycle.signal });
  if (contest) {
    listen(contest, FISHING_CONTEST_SELECTION_CHANGE_EVENT, changed);
    listen(contest, FISHING_CONTEST_VOTING_CLOSE_EVENT, () => {
      if (!votingFromQuery) return;
      votingFromQuery = false;
      (host.querySelector("[data-query-contest-voting]") || input).focus();
    });
  }
  listen(host, "click", event => {
    if (!event.target.closest("[data-query-contest-voting]")) return;
    votingFromQuery = true;
    editing = false; panel.hidden = true;
    contest?.openVoting();
  });
  listen(input, "focus", () => { editing = true; changed(); });
  listen(input, "input", event => { editing = true; highlighted = -1; presentation.pending(event.isComposing); if (!event.isComposing) suggestions(); });
  listen(input, "compositionstart", () => presentation.pending(true));
  listen(input, "compositionend", () => { presentation.pending(false); suggestions(); });
  listen(host, "focusout", event => { if (!host.contains(event.relatedTarget)) { editing = false; panel.hidden = true; } });
  listen(input, "click", () => { editing = true; suggestions(); });
  function accept(index) {
    const candidate = candidates[index];
    if (!candidate) return;
    if (candidate.value.length > 160) return;
    input.value = candidate.value;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    editing = candidate.term.endsWith(":"); panel.hidden = !editing;
  }
  listen(options, "click", event => {
    const button = event.target.closest('[data-query-suggestion-index]');
    if (button) accept(Number(button.dataset.querySuggestionIndex));
  });
  listen(input, "keydown", event => {
    if (event.isComposing) return;
    if (event.key === "Escape") {
      event.preventDefault();
      if (input.value !== presentation.appliedSource) {
        input.value = presentation.appliedSource;
        input.setSelectionRange(input.value.length, input.value.length);
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
      editing = false; panel.hidden = true; return;
    }
    if (event.altKey && ["ArrowDown", "ArrowUp"].includes(event.key) && candidates.length) {
      event.preventDefault(); editing = true;
      highlighted = Math.max(0, Math.min(candidates.length - 1, highlighted + (event.key === "ArrowDown" ? 1 : -1)));
      renderSuggestions();
      options.children[highlighted]?.scrollIntoView({ block: "nearest" });
    } else if (event.key === "Enter" && editing && highlighted >= 0) {
      event.preventDefault(); accept(highlighted);
    }
  });
  function renderSuggestions() {
    options.innerHTML = querySuggestionRowsMarkup(candidates.map(candidate => ({
      primaryMarkup: candidate.term === "contest" ? contestQueryLabelMarkup(t) : candidate.record ? fishQueryRecordMarkup(candidate.record, candidate.kind, t) : fishQueryFilterMarkup(candidate.term.replace(/:$/, ""), candidate.label), kindLabel: candidate.kind ? t(`fishydex.query.field.${candidate.kind}`) : "", insertText: candidate.term,
    })), highlighted);
    panel.hidden = !editing || candidates.length === 0;
  }
  async function loadZones(key, locale, revision) {
    request?.abort();
    const controller = new AbortController(); request = controller;
    zoneKind = "loading";
    try {
      const response = await fetch(`/fishydex/query-zones.${encodeURIComponent(locale)}.json?revision=${encodeURIComponent(revision)}`, { signal: controller.signal });
      if (!response.ok) throw new Error(`Zone index HTTP ${response.status}`);
      const next = decodeZones(await readBoundedAccountJson(response, "Fishydex zone index", 2 * 1024 * 1024), revision, locale);
      if (disposed || request !== controller || key !== zoneKey) return;
      zones = next; zoneKind = "ready"; index = null;
    } catch (error) {
      if (disposed || controller.signal.aborted) return;
      zoneKind = "error";
    }
    changed();
  }
  function recordSuggestion(record, field, prefix, suffix) {
    const kind = record.kind === "fish-group" ? "fish" : record.kind;
    const scope = field === "potential-zone" ? field : kind;
    const exactItem = kind === "fish" && record.id && fishQueryNameGroup(index, record.label);
    const value = kind === "zone" ? record.entry.rgb : exactItem ? record.id : record.label;
    const term = `${scope}:${JSON.stringify(value)}`;
    return { value: prefix + term + suffix, term, label: record.label, kind: scope, record };
  }
  function suggestions() {
    if (!options || !input || !index) return;
    const { field, value, start, end } = fishQueryCompletion(input.value, input.selectionStart ?? input.value.length, index);
    const prefix = input.value.slice(0, start), suffix = input.value.slice(end);
    const group = value && ["text", "fish"].includes(field) && fishQueryNameGroup(index, value);
    const matches = value ? matchingQueryRecords(index, value, field) : [];
    candidates = (group ? [group, ...matches] : matches).slice(0, 12)
      .map(record => recordSuggestion(record, field, prefix, suffix));
    if (["text", "status", "grade", "method"].includes(field)) {
      const terms = ({ status: QUERY_STATUSES, grade: QUERY_GRADES, method: QUERY_METHODS })[field] || ["contest", ...QUERY_STATUSES, ...QUERY_GRADES, ...QUERY_METHODS, "dried"];
      candidates.unshift(...terms.filter(term => term.startsWith(value.toLowerCase())).map(term => ({ value: prefix + (field === "text" ? term : `${field}:${term}`) + suffix, term, label: t(term === "contest" ? "contest.query_title" : `fishydex.query.term.${term}`), kind: "" })));
      if (!value && field === "text") for (const field of ["fish", "title", "zone", "potential-zone"]) {
        const term = `${field}:`;
        candidates.push({ value: prefix + term + suffix, term, label: t(`fishydex.query.field.${field}`), kind: "" });
      }
    } else if (!value && ["fish", "title", "zone", "potential-zone"].includes(field)) {
      candidates = index.records.filter(record => record.kind === (field === "potential-zone" ? "zone" : field)).slice(0, 12)
        .map(record => recordSuggestion(record, field, prefix, suffix));
    }
    renderSuggestions();
  }
  return {
    setTitles(value, kind) {
      if (titleSource === value && titleKind === kind) return;
      titleSource = value; titles = value?.titles || []; titleKind = kind; index = null;
      if (!disposed) changed();
    },
    select(query, context) {
      if (fishSource !== context.fish) { fishSource = context.fish; index = null; }
      const key = `${context.locale}:${context.revision}`;
      if (zoneKey !== key) { request?.abort(); zoneKey = key; zones = []; zoneKind = "empty"; index = null; }
      if (!query && input?.ownerDocument.activeElement !== input) {
        source = query; parsed = applicableFishQuery(query); parsedIndex = index;
        clear.hidden = true; panel.hidden = true;
        return { includesDried: false, matches: () => true, applied: () => presentation.acknowledge({ state: "empty", source: "", draftSource: query, parsed: parsed.parsed, index }) };
      }
      if (!index) index = fishQueryIndex(context.fish, titles, zones);
      if (source !== query || parsedIndex !== index) {
        source = query; parsedIndex = index; parsed = applicableFishQuery(query, index);
      }
      clear.hidden = !input.value;
      suggestions();
      const needsZones = parsed.parsed.fields.has("zone") || parsed.parsed.fields.has("potential-zone") || /(?:^|\s)(?:potential-)?zone:/.test(query);
      const needsTitles = parsed.parsed.requiresTitles;
      const nowSeconds = Date.now() / 1000;
      const contestSelection = contest?.selectionProjection;
      const contestFish = contest?.selectedFish;
      const contestUnavailable = parsed.parsed.fields.has("contest") && !fishingContestSelectionForQuery(contestSelection, nowSeconds);
      if (needsZones && zoneKind === "empty" && context.revision) void loadZones(key, context.locale, context.revision);
      const unavailable = contestUnavailable || (needsZones && zoneKind !== "ready") || (needsTitles && titleKind !== "ready");
      const failed = (needsZones && zoneKind === "error") || (needsTitles && titleKind === "error");
      const state = unavailable ? failed ? "failed" : "applying" : parsed.state;
      const message = unavailable ? t(`fishydex.query.${failed ? "unavailable" : "loading"}`) : "";
      return {
        includesDried: parsed.parsed.fields.has("dried"),
        matches: unavailable ? () => false : compileFishQuery(parsed.parsed, { ...context, index, titles, contestSelection, nowSeconds }),
        applied: () => presentation.acknowledge({ state, source: unavailable ? "" : parsed.source, draftSource: query, parsed: parsed.parsed, index, contestFish, message }),
      };
    },
    dispose() { disposed = true; lifecycle.abort(); request?.abort(); presentation.dispose(); },
  };
}
